import { UserLatestAssessmentResponse } from '@api/assessmentQuestionnaireApiHandler';
import { ActiveFocusArea } from '@api/focusAreasApiHandler';
import { InsightDetails } from '@api/insightsApiHandler';
import GreyTick from '@assets/images/icons/grey-tick.svg';
import MagicIcon from '@assets/images/icons/magic-wand.svg';
import UserProfileIcon from '@assets/images/icons/user-profile-magic.svg';
import { MotionImage, MotionText } from '@shared/atoms';
import { SystemButton } from '@shared/molecules';
import React from 'react';
import { useHistory } from 'react-router';
import { CorePillarScores } from '../CorePillarScores/CorePillarScores';
import styles from './index.module.scss';

interface WellbeingProfileProps {
  lastUpdated: string | null;
  completionPercentage: number;
  boosters: string[];
  drainers: string[];
  focusAreas: ActiveFocusArea[];
  pillars: UserLatestAssessmentResponse[];
  wellbeingInsights: { insightDetails: InsightDetails; questionnairesCompleted: number };
  nextAssessment: UserLatestAssessmentResponse | null;
}

export const WellbeingProfile: React.FC<WellbeingProfileProps> = ({
  lastUpdated,
  completionPercentage,
  boosters,
  drainers,
  focusAreas,
  pillars,
  wellbeingInsights,
  nextAssessment,
}) => {
  const history = useHistory();

  return (
    <section className={styles.profileSection} aria-labelledby="profile-heading">
      <div className={styles.profileHeader}>
        <div className={styles.profileMeta}>
          {lastUpdated ? (
            <p className={styles.lastUpdated}>Last updated on {lastUpdated}</p>
          ) : (
            <></>
          )}
        </div>

        <div className={styles.profileMiddle}>
          <MotionImage
            src={UserProfileIcon}
            alt=""
            aria-hidden="true"
            className={styles.userProfileIcon}
          />

          <MotionText
            id="profile-heading"
            className={styles.sectionTitle}
            as="h3"
            text="Your wellbeing profile"
          />
        </div>

        <MotionText
          className={styles.completionPercentageDesktop}
          as="h5"
          aria-label={`${completionPercentage}% complete`}
          text={`${completionPercentage}%`}
        />
      </div>

      <div className={styles.focusBarDesktop}>
        <div
          style={{
            width: `${completionPercentage}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #329AFB 0%, #329AFB 50%, #F000A8 100%)',
            borderRadius: '0',
            transition: 'width 0.3s ease-in-out',
          }}
        />
      </div>

      <div className={styles.focusBarMobile}>
        <div className={styles.focusBar}>
          <div
            style={{
              width: `${completionPercentage}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #329AFB 0%, #329AFB 50%, #F000A8 100%)',
              borderRadius: '0',
              transition: 'width 0.3s ease-in-out',
            }}
          />
        </div>
        <MotionText
          className={styles.completionPercentageMobile}
          as="h5"
          aria-label={`${completionPercentage}% complete`}
          text={`${completionPercentage}%`}
        />
      </div>

      <div className={styles.profileContent}>
        {wellbeingInsights?.insightDetails?.wellbeingSnapshot && (
          <div className={styles.wellbeingSnapshot}>
            <MotionText className={styles.subsectionTitle} as="h4" text="Wellbeing snapshot" />

            <MotionText
              className={styles.snapshotText}
              as="text"
              text={wellbeingInsights?.insightDetails?.wellbeingSnapshot}
            />
          </div>
        )}

        {wellbeingInsights?.insightDetails?.wellbeingStyle && (
          <div className={styles.wellbeingStyle}>
            <MotionText className={styles.subsectionTitle} as="h4" text="Wellbeing style" />
            <MotionText
              className={styles.styleText}
              as="text"
              text={wellbeingInsights?.insightDetails?.wellbeingStyle}
            />
          </div>
        )}

        {wellbeingInsights?.insightDetails?.patterns &&
          wellbeingInsights?.insightDetails?.patterns?.length > 0 && (
            <div className={styles.patternsSection}>
              <MotionText className={styles.subsectionTitle} as="h4" text="Patterns" />
              <ul className={styles.patternsList} aria-label="Your behavioral patterns">
                {wellbeingInsights?.insightDetails?.patterns.map((pattern, index) => (
                  <li key={index} className={styles.patternItem}>
                    <MotionImage
                      src={GreyTick}
                      alt=""
                      aria-hidden="true"
                      className={styles.tickIcon}
                    />
                    <span className={styles.patternBullet} aria-hidden="true" />
                    <MotionText as="text" text={pattern} />
                  </li>
                ))}
              </ul>
            </div>
          )}

        <div className={styles.boostersDrainers}>
          {boosters && boosters?.length > 0 && (
            <div className={styles.boostersSection}>
              <MotionText className={styles.subsectionTitle} as="h4" text="Your boosters" />

              <div
                className={styles.tagsList}
                role="list"
                aria-label="Factors that boost your wellbeing"
              >
                {boosters.map((booster, index) => (
                  <span
                    key={index}
                    className={`${styles.tag} ${styles.boosterTag}`}
                    role="listitem"
                  >
                    {booster}
                  </span>
                ))}
              </div>
            </div>
          )}

          {drainers && drainers?.length > 0 && (
            <div className={styles.drainersSection}>
              <MotionText className={styles.subsectionTitle} as="h4" text="Your drainer" />

              <div
                className={styles.tagsList}
                role="list"
                aria-label="Factors that drain your wellbeing"
              >
                {drainers.map((drainer, index) => (
                  <span
                    key={index}
                    className={`${styles.tag} ${styles.drainerTag}`}
                    role="listitem"
                  >
                    {drainer}
                  </span>
                ))}
              </div>
            </div>
          )}

          {focusAreas && focusAreas?.length > 0 && (
            <div className={styles.focusAreasSection}>
              <MotionText className={styles.subsectionTitle} as="h4" text="Your priorities" />
              <div className={styles.tagsList} role="list" aria-label="Focus areas">
                {focusAreas.map((area, index) => (
                  <span
                    key={index}
                    className={`${styles.tag} ${styles.focusAreaTag}`}
                    role="listitem"
                  >
                    {area?.questionnairesV2Questions?.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {nextAssessment && (
          <div className={styles.recommendedAction}>
            <MotionImage src={MagicIcon} alt="" aria-hidden="true" className={styles.magicIcon} />

            {wellbeingInsights?.questionnairesCompleted >= 3 ? (
              <>
                <MotionText
                  className={styles.recommendedActionTitle}
                  as="h4"
                  text="Recommended Next Action"
                />
                <MotionText
                  className={styles.recommendedActionText}
                  as="p"
                  text={`Complete ${
                    nextAssessment?.name || 'Next'
                  } assessment to unlock more insights.`}
                />

                {nextAssessment && (
                  <SystemButton
                    className={styles.actionButton}
                    aria-label="Take next assessment"
                    onClick={() =>
                      history.push(`/assessment?questionnaireId=${nextAssessment?.id}`)
                    }
                  >
                    Take {nextAssessment?.name || 'Next'} Assessment
                  </SystemButton>
                )}
              </>
            ) : (
              <>
                <MotionText
                  className={styles.recommendedActionTitle}
                  as="h4"
                  text="Unlock Your Full Wellbeing Profile"
                />
                <MotionText
                  className={styles.recommendedActionTextNotCompleted}
                  as="p"
                  text="Complete 3 core assessments to unlock your personalised insights, wellbeing style
                and recommendations tailored just for you."
                />

                <div className={styles.dotContainer}>
                  {[...Array(3)].map((_, index) => (
                    <div
                      key={index}
                      className={
                        index < wellbeingInsights?.questionnairesCompleted
                          ? styles.filledDot
                          : styles.outlineDot
                      }
                    />
                  ))}
                </div>

                {nextAssessment && (
                  <SystemButton
                    className={styles.actionButton}
                    aria-label="Take next assessment"
                    onClick={() =>
                      history.push(`/assessment?questionnaireId=${nextAssessment?.id}`)
                    }
                  >
                    Take {nextAssessment?.name || 'Next'} Assessment
                  </SystemButton>
                )}
              </>
            )}
          </div>
        )}

        <CorePillarScores pillars={pillars} nextAssessment={nextAssessment} />
      </div>
    </section>
  );
};
